import _ from 'lodash';
import moment from 'moment';
import api from '@/utils/api';
import XLSX_STYLE from 'xlsx-js-style';
import ExportExcelTemplate from './ExportExcelTemplate';

// 取得資料
export async function fetchRowDatas({
    SerialNumber, StartDateStart, StartDateEnd, TransactionType, Status, Cnl,
}) {
    const url = '/AccountingSummaryPage/QueryVouchers';
    const queryParams = {
        UNT_NBR: null,
        SerialNumber,
        StartDateStart,
        StartDateEnd,
        Status,
        TransactionType,
        Cnl,
        ShowDetail: true,
        PageSize: 1000,
        PageNum: 1,
    };

    try {
        const { data } = await api({
            url,
            method: 'GET',
            params: queryParams,
        });

        // 足夠的PageSize
        if (queryParams.PageSize > data.totalNum) {
            return data.items;
        }

        const currentList = [...data.items];
        for (let i = 0; i < Math.ceil(data.totalNum / queryParams.PageSize) - 1; i += 1) {
            queryParams.PageNum += 1;
            // eslint-disable-next-line no-await-in-loop
            const resp = await api({
                url,
                method: 'GET',
                params: queryParams,
            });
            currentList.push(...resp.data.items);
        }
        console.log(currentList);
        return currentList;
    } catch (ex) {
        console.log(ex);
        return [];
    }
}

// 將API資料整理成 excel 的資料
export function formatDataRows(rawDatas) {
    const rows = [];
    const rowDivider = [];

    rawDatas.forEach((item, i) => {
        if (!rowDivider.length) rowDivider.push(item.accountItems.length);
        else rowDivider.push(rowDivider[i - 1] + item.accountItems.length);

        item.accountItems.forEach((accountItem) => {
            rows.push([
                moment(item.dat).format('YYYY-MM-DD'),
                item.serialNumber,
                accountItem.accno,
                accountItem.accnonam,
                accountItem.dc_Type === 1 ? '借' : '貸',
                accountItem.content || '',
                accountItem.crD_NTD || '',
                accountItem.deB_NTD || '',
            ]);
        });
    });

    return { rows, rowDivider };
}

// 建立 Excel 工作表
export function createExcelSheet({ StartDateStart, StartDateEnd }, rawDatas, companyName) {
    const { rows, rowDivider } = formatDataRows(rawDatas);
    const sheet = { ...ExportExcelTemplate };

    XLSX_STYLE.utils.sheet_add_aoa(sheet, rows, { origin: 'A5' });

    const widthSettings = [12, 12, 12, 12, 8, 55, 10, 10];
    // 設定全部欄寬 (由bodyHeader定義，預設 15)
    sheet['!cols'] = _.map(widthSettings, (h) => ({ wch: h }));
    sheet['!rows'] = [{ hpx: 70 }, { hpx: 25 }, { hpx: 25 }];

    _.forEach(sheet, (value, key) => {
        if (!key.includes('!')) {
            // 'B13' => COL: 'B', ROW: '13'
            const isLetterSingle = !Number.isNaN(Number(key.substring(1)));
            const COL = isLetterSingle ? key.substring(0, 1) : key.substring(0, 2);
            const ROW = isLetterSingle ? Number(key.substring(1)) : Number(key.substring(2));

            // 每一格
            sheet[key].s = {
                alignment: {
                    horizontal: 'left',
                    vertical: 'bottom',
                },
            };

            // 公司名稱標題
            if (key === 'A1') {
                sheet[key].s.alignment.horizontal = 'center';
                sheet[key].s.alignment.vertical = 'center';
                sheet[key].s.alignment.wrapText = true;

                sheet[key].v = `${companyName}\n日記帳（傳票清單）`;

                sheet[key].s.font = {
                    bold: true,
                    sz: 16,
                };
            }

            // 選取區間
            if (key === 'A2') {
                sheet[key].s.alignment.horizontal = 'center';
                sheet[key].s.alignment.vertical = 'center';

                const START_DATE = moment(StartDateStart);
                const END_DATE = moment(StartDateEnd);
                sheet[key].v = `民國 ${START_DATE.year() - 1911} 年 ${START_DATE.format('MM 月 DD 日')} ～ 民國 ${END_DATE.year() - 1911} 年 ${END_DATE.format('MM 月 DD 日')}`;

                sheet[key].s.font = {
                    bold: true,
                    sz: 13,
                };
            }

            // 製表日期
            if (key === 'A3') {
                sheet[key].s.alignment.horizontal = 'right';
                sheet[key].s.alignment.vertical = 'center';
                const TODAY = moment();
                sheet[key].v = `製表日期：${TODAY.year() - 1911} 年 ${TODAY.format('MM 月 DD 日')}`;
            }

            // 表頭
            if (ROW === 4) {
                sheet[key].s.border = {
                    bottom: { style: 'medium', color: 'FFFFFFFF' },
                    top: { style: 'medium', color: 'FFFFFFFF' },
                };
                sheet[key].s.alignment.horizontal = 'center';
            }

            // 借、貸方金額格式設定
            if (ROW > 4 && (COL === 'G' || COL === 'H')) {
                sheet[key].s.alignment.horizontal = 'right';
                sheet[key].z = '#,##0_-;-#,##0_-;0;General';
            }

            // 分隔線設定
            if (rowDivider.includes(ROW - 4)) sheet[key].s.border = { bottom: { style: 'medium', color: 'FFFFFFFF' } };
        }
    });

    return sheet;
}

// 下載 Excel
export async function downloadExcel(queryParams, companyName, rawDatas = null, directDownload = true) {
    const datas = rawDatas || (await fetchRowDatas(queryParams)); // 如果沒有給資料，就重新拿一份
    const excelBook = XLSX_STYLE.utils.book_new();
    const excelSheet = createExcelSheet(queryParams, datas, companyName);
    const fileName = `${moment(+queryParams.StartDateStart).format('YYYY-MM-DD')}～${moment(+queryParams.StartDateEnd).format('YYYY-MM-DD')}傳票清單.xlsx`;

    XLSX_STYLE.utils.book_append_sheet(excelBook, excelSheet, '傳票清單');
    if (directDownload) {
        return XLSX_STYLE.writeFile(excelBook, fileName);
    }
    const excelBuffer = XLSX_STYLE.write(excelBook, { type: 'array', bookType: 'xlsx' });
    return { buffer: excelBuffer, fileName };
}
